import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../../../components/toast-store";
import { useAuthSession } from "../../shared";
import { merchantOnboardingApi } from "../api/merchantOnboardingApi";
import {
  getMerchantOnboardingPath,
  getMerchantRegistrationRedirect,
} from "../utils/merchantOnboarding";

interface MerchantOnboardingSessionRecoveryProps {
  children?: React.ReactNode;
  requestedPath?: string;
}

/** Synchronizes canonical onboarding status before rendering an authenticated merchant route. */
export const MerchantOnboardingSessionRecovery = ({
  children,
  requestedPath,
}: MerchantOnboardingSessionRecoveryProps) => {
  const hasRecovered = useRef(false);
  const navigate = useNavigate();
  const { clearSession, updateMerchantOnboarding } = useAuthSession();

  useEffect(() => {
    if (hasRecovered.current) return;
    hasRecovered.current = true;

    const recover = async () => {
      try {
        const status = await merchantOnboardingApi.getStatus();
        updateMerchantOnboarding(status.stage, status.is_onboarding_completed);
        const redirectTo = requestedPath
          ? getMerchantRegistrationRedirect({
              isLoggedIn: true,
              role: "merchant",
              requestedPath,
              onboardingStage: status.stage,
            })
          : getMerchantOnboardingPath(status.stage);

        if (redirectTo) {
          navigate(redirectTo, { replace: true });
          return;
        }
      } catch {
        clearSession();
        showToast({
          type: "warning",
          title: "Please sign in again",
          message: "We could not determine your onboarding status.",
        });
        navigate("/login", { replace: true });
      }
    };

    void recover();
  }, [clearSession, navigate, requestedPath, updateMerchantOnboarding]);

  return <>{children}</>;
};
